import { createReducer, on } from '@ngrx/store';
import * as DashboardActions from './dashboard.actions';
import * as AuthActions from '../auth/auth.actions';
import { DashboardDataState, generateCacheKey, initialDashboardDataState } from './dashboard.state';

export const dashboardReducer = createReducer(
  initialDashboardDataState,
  on(AuthActions.resetAllState, () => {
    return initialDashboardDataState;
  }),
  
  on(DashboardActions.loadDashboardWidgetData, (state, { widgetType, dataSource, filters }) => {
    const cacheKey = generateCacheKey(widgetType, dataSource, filters);
    
    return {
      ...state,
      widgetData: {
        ...state.widgetData,
        [cacheKey]: {
          ...state.widgetData[cacheKey],
          loading: true,
          error: null
        }
      }
    };
  }),
  
  on(DashboardActions.loadDashboardWidgetDataSuccess, (state, { widgetType, dataSource, filters, data }) => {
    const cacheKey = generateCacheKey(widgetType, dataSource, filters);
    
    return {
      ...state,
      widgetData: {
        ...state.widgetData,
        [cacheKey]: {
          data,
          loading: false,
          error: null,
          lastUpdated: Date.now()
        }
      }
    };
  }),
  
  on(DashboardActions.loadDashboardWidgetDataFailure, (state, { widgetType, dataSource, filters, error }) => {
    const cacheKey = generateCacheKey(widgetType, dataSource, filters);
    
    return {
      ...state,
      widgetData: {
        ...state.widgetData,
        [cacheKey]: {
          ...state.widgetData[cacheKey],
          loading: false,
          error
        }
      }
    };
  }),
  
  on(DashboardActions.clearDashboardWidgetData, (state, { widgetType }) => {
    if (!widgetType) {
      // Clear all widget data
      return {
        ...state,
        widgetData: {}
      };
    }
    
    // Create a new widget data object without entries for the specified widget type
    const newWidgetData = { ...state.widgetData };
    
    // Remove entries for the specified widget type
    Object.keys(newWidgetData).forEach(key => {
      if (key.startsWith(`${widgetType}:`)) {
        delete newWidgetData[key];
      }
    });
    
    return {
      ...state,
      widgetData: newWidgetData
    };
  }),

  // Handle user-specific widget configuration actions
  on(DashboardActions.setCurrentUser, (state, { userId }) => {
    return {
      ...state,
      currentUserId: userId
    };
  }),

  on(DashboardActions.loadUserWidgetConfigsSuccess, (state, { userId, configs }) => {
    const userConfigs = { ...state.userWidgetConfigs };
    
    // Convert configs to the format with lastUpdated timestamp
    const userWidgetConfigs: { [widgetId: string]: any } = {};
    Object.keys(configs).forEach(widgetId => {
      userWidgetConfigs[widgetId] = {
        ...configs[widgetId],
        lastUpdated: Date.now()
      };
    });
    
    userConfigs[userId] = userWidgetConfigs;
    
    return {
      ...state,
      userWidgetConfigs: userConfigs
    };
  }),

  on(DashboardActions.saveUserWidgetConfig, (state, { userId, widgetId, settings, position }) => {
    // Make sure we have an entry for this user
    const userConfigs = { ...(state.userWidgetConfigs || {}) };
    
    if (!userConfigs[userId]) {
      userConfigs[userId] = {};
    }
    
    // Update the specific widget config
    userConfigs[userId] = {
      ...userConfigs[userId],
      [widgetId]: {
        settings,
        position,
        lastUpdated: Date.now()
      }
    };
    
    return {
      ...state,
      userWidgetConfigs: userConfigs
    };
  }),

  on(DashboardActions.clearUserWidgetConfigs, (state, { userId }) => {
    if (!userId) {
      // Clear all user configs
      return {
        ...state,
        userWidgetConfigs: {}
      };
    }
    
    // Only clear configs for the specified user
    const userConfigs = { ...state.userWidgetConfigs };
    delete userConfigs[userId];
    
    return {
      ...state,
      userWidgetConfigs: userConfigs
    };
  })
); 